import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import QuestionReview from "../../components/QuestionReview";
import { Logo } from "../../components/site/Seo";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Trophy, Target, Percent, Award, Bookmark, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const Metric = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div className="card-surface p-5" data-testid={testid}>
    <Icon className={`w-5 h-5 mb-3 ${accent || "text-ember"}`} />
    <div className="font-heading font-bold text-2xl">{value}</div>
    <div className="text-xs text-content-muted uppercase tracking-wide mt-1">{label}</div>
    {sub && <div className="text-xs text-content-secondary mt-1">{sub}</div>}
  </div>
);

export default function Result() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("summary");

  useEffect(() => { api.get(`/attempts/${attemptId}/result`).then((r) => setD(r.data)).catch(() => nav("/student/dashboard")); }, [attemptId]);
  if (!d) return <div className="min-h-screen bg-ink flex items-center justify-center text-content-muted">Loading result…</div>;

  const r = d.attempt.result;
  const subjectData = Object.entries(r.subject_scores || {}).map(([s, v]) => ({ subject: s.slice(0, 10), accuracy: v.total ? Math.round(v.correct / v.total * 100) : 0 }));
  const strong = subjectData.filter((s) => s.accuracy >= 60).map((s) => s.subject);
  const weak = subjectData.filter((s) => s.accuracy < 60).map((s) => s.subject);

  const toggleBookmark = async (qid) => {
    const { data } = await api.post("/bookmarks/toggle", { question_id: qid });
    toast.success(data.bookmarked ? "Bookmarked" : "Removed bookmark");
    setD((p) => ({ ...p, review: p.review.map((q) => q.id === qid ? { ...q, bookmarked: data.bookmarked } : q) }));
  };

  return (
    <div className="min-h-screen bg-ink">
      <header className="glass-nav border-b border-line px-4 h-14 flex items-center justify-between">
        <Logo className="h-7" />
        <Button asChild size="sm" variant="ghost"><Link to="/student/dashboard"><ArrowLeft className="w-4 h-4 mr-1" /> Dashboard</Link></Button>
      </header>
      <div className="container-px py-8 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-widest text-ember mb-2">Result</p>
        <h1 className="font-heading text-2xl font-bold mb-6">{d.attempt.test_name}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Metric icon={Trophy} label="Score" value={`${r.score}`} sub={`out of ${r.total_marks}`} testid="analytics-overall-score" />
          <Metric icon={Percent} label="Percentage" value={`${r.percentage}%`} testid="result-percentage" />
          <Metric icon={Target} label="Accuracy" value={`${r.accuracy}%`} sub={`${r.correct} correct / ${r.wrong} wrong`} accent="text-emerald-400" testid="result-accuracy" />
          <Metric icon={Award} label="Percentile" value={`${r.percentile}%`} sub={`Rank #${r.rank} of ${r.total_takers}`} testid="analytics-percentile-rank" />
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-8">
          <Mini label="Attempted" value={r.attempted} />
          <Mini label="Correct" value={r.correct} c="text-emerald-400" />
          <Mini label="Wrong" value={r.wrong} c="text-red-400" />
          <Mini label="Skipped" value={r.unattempted} c="text-content-muted" />
          <Mini label="Total Qs" value={r.total_questions} />
        </div>

        <div className="flex gap-2 mb-6 border-b border-line">
          {["summary", "solutions"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm capitalize border-b-2 -mb-px ${tab === t ? "border-ember text-ember" : "border-transparent text-content-secondary"}`} data-testid={`result-tab-${t}`}>{t === "summary" ? "Analysis" : "Solutions"}</button>
          ))}
        </div>

        {tab === "summary" ? (
          <div className="space-y-6">
            <div className="card-surface p-5">
              <h3 className="font-heading font-semibold text-sm mb-4">Subject-wise accuracy</h3>
              {subjectData.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={subjectData}>
                    <XAxis dataKey="subject" stroke="#64748B" fontSize={11} tickLine={false} />
                    <YAxis stroke="#64748B" fontSize={11} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#151F30", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Bar dataKey="accuracy" radius={[6, 6, 0, 0]}>
                      {subjectData.map((s, i) => <Cell key={i} fill={s.accuracy >= 60 ? "#10B981" : "#F97316"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="text-sm text-content-muted">No subject data.</div>}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="card-surface p-5"><h4 className="text-sm font-medium text-emerald-400 mb-2">Strengths</h4><p className="text-sm text-content-secondary">{strong.length ? strong.join(", ") : "Attempt more to identify strengths."}</p></div>
              <div className="card-surface p-5"><h4 className="text-sm font-medium text-ember mb-2">Focus areas</h4><p className="text-sm text-content-secondary">{weak.length ? weak.join(", ") : "Great — no major weak areas!"}</p></div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {d.review.map((q) => (
              <div key={q.id} className="relative">
                <button onClick={() => toggleBookmark(q.id)} className="absolute top-4 right-4 z-10" data-testid={`bookmark-${q.id}`}>
                  <Bookmark className={`w-4 h-4 ${q.bookmarked ? "fill-ember text-ember" : "text-content-muted"}`} />
                </button>
                <QuestionReview q={q} showUser />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
const Mini = ({ label, value, c }) => (
  <div className="card-surface p-3 text-center"><div className={`font-heading font-bold text-lg ${c || ""}`}>{value}</div><div className="text-[10px] text-content-muted uppercase">{label}</div></div>
);
